import PropTypes from "prop-types";

import { Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { formatDate, formatMoney } from "@/features/shared";
import { UpstreamTransactionLink } from "@/features/ui";
import { ReturnableTypeEnum, ReturnableTypeLabel } from "../../returnable-type";

export const GeneralInformationSection = ({ purchaseReturn }) => {
  // Check if this is a Non-Faktur return (empty PI with PI-PRT prefix)
  const isNonInvoiceReturn =
    purchaseReturn.returnableType === ReturnableTypeEnum.PURCHASE_INVOICE &&
    purchaseReturn.returnable?.referenceNumber?.startsWith("PI-PRT");

  const displayReturnableType = isNonInvoiceReturn
    ? ReturnableTypeEnum.NON_INVOICE
    : purchaseReturn.returnableType;

  const showPricing =
    purchaseReturn.returnableType === ReturnableTypeEnum.PURCHASE_INVOICE ||
    purchaseReturn.returnableType === ReturnableTypeEnum.NON_INVOICE;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1">Informasi Umum</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Dibuat oleh
          </Typography>
          <Typography variant="body1">
            {purchaseReturn.creator?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Tanggal Transaksi
          </Typography>
          <Typography variant="body1">
            {formatDate(purchaseReturn.transactionDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pemasok
          </Typography>
          <Typography variant="body1">
            {purchaseReturn.seller?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Outlet
          </Typography>
          <Typography variant="body1">
            {purchaseReturn.buyer?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Jenis Retur
          </Typography>
          <Typography variant="body1">
            {displayReturnableType
              ? ReturnableTypeLabel[displayReturnableType]
              : "Non Faktur"}
          </Typography>
        </Grid>
        {!isNonInvoiceReturn && purchaseReturn.returnableType && (
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Return Dari
            </Typography>
            <UpstreamTransactionLink
              referenceNumber={purchaseReturn.returnable?.referenceNumber}
              shortReference={false}
            />
          </Grid>
        )}
        {showPricing && (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Subtotal
              </Typography>
              <Typography variant="body1">
                {formatMoney(purchaseReturn.subtotal)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Pajak
              </Typography>
              <Typography variant="body1">
                {formatMoney(purchaseReturn.taxAmount)} (
                {purchaseReturn.taxPercentage}%)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Diskon
              </Typography>
              <Typography variant="body1">
                {purchaseReturn.discountType === "PERCENTAGE"
                  ? `${purchaseReturn.discountAmount ?? 0}% (~${formatMoney(
                      ((purchaseReturn.subtotal ?? 0) *
                        (purchaseReturn.discountAmount ?? 0)) /
                        100
                    )})`
                  : formatMoney(purchaseReturn.discountAmount ?? 0)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Total Diskon
              </Typography>
              <Typography variant="body1">
                {formatMoney(
                  purchaseReturn.totalDiscount ?? purchaseReturn.discount ?? 0
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Grand Total
              </Typography>
              <Typography variant="body1" sx={{
                fontWeight: "bold"
              }}>
                {formatMoney(purchaseReturn.grandTotal)}
              </Typography>
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Catatan
          </Typography>
          <Typography variant="body1">{purchaseReturn.note ?? "-"}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  purchaseReturn: PropTypes.object.isRequired,
};
